import { useMutation, useQueryClient } from '@tanstack/react-query'

import { USER_QUERY_KEYS } from '../api/endpoints'
import type { UserApi } from '../api/userApi'
import type {
  PbxUser,
  PbxUserCreate,
  PbxUserUpdate,
  UserCreate,
  UserDetail,
  UserUpdate,
} from '../types/user.types'

// ─── Error class ─────────────────────────────────────────────────────────────

export type UserErrorCode = 'not_found' | 'forbidden' | 'validation' | 'unknown'

export class UserError extends Error {
  constructor(
    public readonly code: UserErrorCode,
    message: string,
    public readonly rawErrorData?: unknown,
  ) {
    super(message)
    this.name = 'UserError'
  }
}

function extractValidationMessage(errorData: unknown): string {
  if (typeof errorData === 'string' && errorData.trim().length > 0) {
    return errorData
  }

  if (!errorData || typeof errorData !== 'object') return 'Validation error'

  const data = errorData as Record<string, unknown>

  // Collect human-readable message strings only — no key labels.
  const messages: string[] = []

  // Top-level message / detail
  for (const key of ['message', 'detail'] as const) {
    const val = data[key]
    if (typeof val === 'string' && val.trim()) {
      messages.push(val.trim())
    }
  }

  // `parameters` array: [{name, code, message}, ...]
  if (Array.isArray(data.parameters)) {
    for (const item of data.parameters) {
      if (item && typeof item === 'object') {
        const msg = (item as Record<string, unknown>).message
        if (typeof msg === 'string' && msg.trim()) {
          messages.push(msg.trim())
        }
      }
    }
  }

  // `non_field_errors` / `errors` arrays of strings
  for (const key of ['non_field_errors', 'errors'] as const) {
    const val = data[key]
    if (Array.isArray(val)) {
      for (const item of val) {
        if (typeof item === 'string' && item.trim()) messages.push(item.trim())
      }
    }
  }

  // Standard DRF field-keyed errors: {username: ["Already taken."]}
  const metadataKeys = new Set([
    'type',
    'code',
    'status',
    'title',
    'detail',
    'message',
    'non_field_errors',
    'parameters',
    'errors',
  ])
  for (const [key, value] of Object.entries(data)) {
    if (metadataKeys.has(key)) continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) messages.push(item.trim())
      }
    } else if (typeof value === 'string' && value.trim()) {
      messages.push(value.trim())
    }
  }

  const deduped = Array.from(new Set(messages)).filter(Boolean)
  return deduped.length > 0 ? deduped.join(' ') : 'Validation error'
}

/**
 * Extracts per-field error messages from a DRF-style 400 response body.
 * Returns a Record mapping field names to their first error string.
 * Non-field errors (e.g. `non_field_errors`, `detail`) go under `_nonField`.
 *
 * Handles two formats:
 *  1. Standard DRF:       { username: ["Already taken."] }
 *  2. parameters array:   { parameters: [{ name: "username", message: "..." }] }
 */
export function extractFieldErrors(errorData: unknown): Record<string, string> {
  if (!errorData || typeof errorData !== 'object') return {}

  const data = errorData as Record<string, unknown>
  const result: Record<string, string> = {}

  const appendToResult = (key: string, message: string) => {
    const trimmed = message.trim()
    if (!trimmed) return
    if (result[key]) {
      result[key] = `${result[key]} ${trimmed}`
    } else {
      result[key] = trimmed
    }
  }

  // Format 2: parameters array [{name, code, message}, ...]
  if (Array.isArray(data.parameters)) {
    for (const item of data.parameters) {
      if (!item || typeof item !== 'object') continue
      const { name, message } = item as Record<string, unknown>
      if (typeof name === 'string' && typeof message === 'string') {
        appendToResult(name, message)
      }
    }
    // If we got field entries from `parameters`, return them (possibly
    // alongside a top-level non-field message).
    if (Object.keys(result).length > 0) {
      // Also capture any top-level non-field message
      const topMessage = data.message
      if (typeof topMessage === 'string' && topMessage.trim()) {
        const fieldsCoveredByParams = new Set(Object.keys(result))
        // Only promote top-level message to _nonField if it's not already
        // represented as a field error.
        if (!fieldsCoveredByParams.has('_nonField')) {
          // Skip if the top-level message is just a generic envelope phrase
          // and field-specific messages already cover the user-facing detail.
          // We still expose it so callers can decide.
          appendToResult('_nonField', topMessage)
        }
      }
      return result
    }
  }

  // Format 1: standard DRF field-keyed errors
  const nonFieldKeys = new Set(['non_field_errors', 'detail', 'message'])
  const metadataOnlyKeys = new Set(['type', 'code', 'status', 'title'])

  for (const [key, value] of Object.entries(data)) {
    if (metadataOnlyKeys.has(key)) continue

    const message = flattenFieldValue(value)
    if (!message) continue

    if (nonFieldKeys.has(key)) {
      appendToResult('_nonField', message)
    } else {
      appendToResult(key, message)
    }
  }

  return result
}

function flattenFieldValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean)
    return messages.length > 0 ? messages.join(' ') : null
  }
  return null
}

function mapStatusToError(
  status: number | undefined,
  errorData: unknown,
): UserError {
  if (status === 400)
    return new UserError(
      'validation',
      extractValidationMessage(errorData),
      errorData,
    )
  if (status === 403)
    return new UserError(
      'forbidden',
      'You do not have permission to perform this action',
    )
  if (status === 404) return new UserError('not_found', 'Resource not found')
  return new UserError('unknown', 'An unexpected error occurred')
}

// ─── Remove user from PBX ─────────────────────────────────────────────────────

export type RemoveUserErrorCode = 'not_found' | 'forbidden' | 'unknown'

export class RemoveUserError extends Error {
  constructor(
    public readonly code: RemoveUserErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'RemoveUserError'
  }
}

export interface RemoveFromPbxVariables {
  pbxId?: number
  userId: number
  reassignAssetsTo?: number
}

export const useRemoveUserFromPbx = ({
  api,
  pbxId,
}: {
  api: UserApi
  pbxId?: number
}) => {
  const queryClient = useQueryClient()

  return useMutation<void, RemoveUserError, RemoveFromPbxVariables>({
    mutationFn: async ({ pbxId: mutationPbxId, userId, reassignAssetsTo }) => {
      const effectivePbxId = mutationPbxId ?? pbxId
      if (!effectivePbxId) {
        throw new RemoveUserError('unknown', 'PBX id is required')
      }

      const response = await api.removeUserFromPbx(
        effectivePbxId,
        userId,
        reassignAssetsTo,
      )
      if (response.ok) return

      if (response.status === 404) {
        throw new RemoveUserError(
          'not_found',
          'User is not a member of this PBX',
        )
      }
      if (response.status === 403) {
        throw new RemoveUserError(
          'forbidden',
          'You do not have permission to perform this action',
        )
      }
      throw new RemoveUserError('unknown', 'Failed to remove user from PBX')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.details() })
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userPbxLists(),
      })
    },
  })
}

// ─── Create user ──────────────────────────────────────────────────────────────

export const useCreateUser = ({ api }: { api: UserApi }) => {
  const queryClient = useQueryClient()

  return useMutation<UserDetail | null, UserError, UserCreate>({
    mutationFn: async (data) => {
      const response = await api.createUser(data)
      if (response.ok) return response.data ?? null
      throw mapStatusToError(response.status, response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.globalLists(),
      })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() })
    },
  })
}

// ─── Update user ──────────────────────────────────────────────────────────────

export interface UpdateUserVariables {
  userId: number
  data: UserUpdate
}

export const useUpdateUser = ({ api }: { api: UserApi }) => {
  const queryClient = useQueryClient()

  return useMutation<UserDetail | null, UserError, UpdateUserVariables>({
    mutationFn: async ({ userId, data }) => {
      const response = await api.updateUser(userId, data)
      if (response.ok) return response.data ?? null
      throw mapStatusToError(response.status, response.data)
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.detail(userId),
      })
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.globalLists(),
      })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() })
    },
  })
}

// ─── Add user to PBX ─────────────────────────────────────────────────────────

export interface AddUserToPbxVariables {
  pbxId: number
  data: PbxUserCreate
}

export const useAddUserToPbx = ({ api }: { api: UserApi }) => {
  const queryClient = useQueryClient()

  return useMutation<PbxUser | null, UserError, AddUserToPbxVariables>({
    mutationFn: async ({ pbxId, data }) => {
      const response = await api.addUserToPbx(pbxId, data)
      if (response.ok) return response.data ?? null
      throw mapStatusToError(response.status, response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.details() })
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userPbxLists(),
      })
    },
  })
}

// ─── Reset password ──────────────────────────────────────────────────────────

export const useResetPassword = ({ api }: { api: UserApi }) => {
  return useMutation<
    { reset_url: string } | null,
    UserError,
    { userId: number }
  >({
    mutationFn: async ({ userId }) => {
      const response = await api.resetPassword(userId)
      if (response.ok) return response.data ?? null
      throw mapStatusToError(response.status, response.data)
    },
  })
}

// ─── Update PBX user ──────────────────────────────────────────────────────────

export interface UpdatePbxUserVariables {
  pbxId: number
  userId: number
  data: PbxUserUpdate
}

export const useUpdatePbxUser = ({ api }: { api: UserApi }) => {
  const queryClient = useQueryClient()

  return useMutation<PbxUser | null, UserError, UpdatePbxUserVariables>({
    mutationFn: async ({ pbxId, userId, data }) => {
      const response = await api.updatePbxUser(pbxId, userId, data)
      if (response.ok) return response.data ?? null
      throw mapStatusToError(response.status, response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.details() })
      queryClient.invalidateQueries({
        queryKey: USER_QUERY_KEYS.userPbxLists(),
      })
    },
  })
}
